import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useEncryption } from "@/hooks/useEncryption";
import { Formik, Form, Field } from "formik";
import { type } from "arktype";
import { arktypeToFormikValidate } from "@/lib/validation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { IconPicker } from "@/components/shared/IconPicker";
import { showToast } from "@/components/shared/Toast";
import { BottomDrawer } from "@/components/shared/BottomDrawer";
import { Plus, X, Zap, Timer } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useConfirm } from "@/components/shared/ConfirmDialog";

const ruleSchema = type({
  name: "string >= 1",
  icon: "string",
  description: "string",
  streak_factor: "number >= 0",
  punishment_points: "number.integer >= 0",
});
const validateRule = arktypeToFormikValidate(ruleSchema);

interface Rule {
  _key: string;
  name: string;
  notification_name: string;
  icon: string;
  description: string;
  is_active: boolean;
  streak_factor: number;
  punishment_points: number;
  max_points: number | null;
  expiry_days: number | null;
  expires_at: string | null;
}

const EXPIRY_PRESETS = [
  { label: "1d", days: 1 },
  { label: "3d", days: 3 },
  { label: "5d", days: 5 },
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

export function AdminRules() {
  const { jwt, clientId } = useParams();
  const { getHeaders } = useAuth();
  const { encryptFields, decryptList, hasKey } = useEncryption(clientId);
  const { data: rawData, loading, error, refetch } = useApi<{ rules: Rule[]; current_points: number }>(
    `/api/admin/${jwt}/${clientId}/rules`,
  );
  const currentPoints = rawData?.current_points ?? 0;
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [restartTimerOnSave, setRestartTimerOnSave] = useState(false);
  const [customExpiry, setCustomExpiry] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  // Decrypt rule names/descriptions after fetch
  useEffect(() => {
    if (!rawData?.rules) return;
    decryptList(rawData.rules as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setRules(d as unknown as Rule[]));
  }, [rawData?.rules, decryptList]);

  const handleSubmit = useCallback(async (rawValues: Record<string, unknown>) => {
    const base = {
      ...rawValues,
      max_points: rawValues.max_points === "" || rawValues.max_points === undefined ? null : Number(rawValues.max_points),
      expiry_days: rawValues.expiry_days === "" || rawValues.expiry_days === undefined || rawValues.expiry_days === null ? null : Number(rawValues.expiry_days),
    };
    if (restartTimerOnSave || !editingRule) {
      (base as any).restart_timer = true;
    }
    // Encrypt name and description before sending
    const values = await encryptFields(base as Record<string, unknown>, ["name", "description"]);
    setSubmitLoading(true);
    try {
      const url = editingRule
        ? `/api/admin/${jwt}/${clientId}/rules/${editingRule._key}`
        : `/api/admin/${jwt}/${clientId}/rules`;
      const method = editingRule ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(values) });
      if (!res.ok) throw new Error((await res.json()).error);
      setShowForm(false);
      setEditingRule(null);
      setRestartTimerOnSave(false);
      refetch();
      showToast.success("Rule saved!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitLoading(false);
    }
  }, [editingRule, jwt, clientId, encryptFields, getHeaders, refetch, restartTimerOnSave]);

  const handleDelete = useCallback(async (key: string) => {
    const ok = await confirm({
      title: "Deactivate this rule?",
      description: "The rule will be hidden but its history preserved.",
      confirmLabel: "Deactivate",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`/api/admin/${jwt}/${clientId}/rules/${key}`, { method: "DELETE", headers: getHeaders() });
    refetch();
  }, [jwt, clientId, getHeaders, refetch, confirm]);

  const handlePermanentDelete = useCallback(async (key: string) => {
    const ok = await confirm({
      title: "Permanently delete this rule?",
      description: "This will remove the rule and its edge completely. This cannot be undone.",
      confirmLabel: "Delete Forever",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`/api/admin/${jwt}/${clientId}/rules/${key}/permanent`, { method: "DELETE", headers: getHeaders() });
    refetch();
  }, [jwt, clientId, getHeaders, refetch, confirm]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-heading font-bold">Rules</h1>
        <Button onClick={() => { setShowForm(true); setEditingRule(null); }} className="bg-app-primary hover:bg-app-primary-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      <BottomDrawer open={showForm} onClose={() => { setShowForm(false); setEditingRule(null); setRestartTimerOnSave(false); setCustomExpiry(false); }} title={editingRule ? "✏️ Edit Rule" : "✨ New Rule"}>
        {showForm && (
          <Formik
            initialValues={{
              name: editingRule?.name || "",
              notification_name: editingRule?.notification_name || "",
              icon: editingRule?.icon || "📏",
              description: editingRule?.description || "",
              streak_factor: editingRule?.streak_factor || 1,
              punishment_points: editingRule?.punishment_points || 0,
              max_points: editingRule?.max_points ?? "",
              expiry_days: editingRule?.expiry_days ?? "",
            }}
            validate={validateRule}
            onSubmit={(v) => { setCustomExpiry(false); handleSubmit(v); }}
          >
            {({ values, errors, touched, setFieldValue, submitForm }) => {
              const hasTimer = editingRule && editingRule.expires_at && editingRule.expiry_days;
              return (
              <Form className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="space-y-1">
                    <Label>Icon</Label>
                    <IconPicker value={values.icon} onSelect={(emoji) => setFieldValue("icon", emoji)} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label>Name</Label>
                    <Field as={Input} name="name" placeholder="Rule name" autoFocus />
                    {touched.name && errors.name && <p className="text-info text-app-danger">{errors.name}</p>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Notification Name (optional, unencrypted)</Label>
                  <Field as={Input} name="notification_name" placeholder="e.g., 'Bedtime rule' - shown in push notifications" />
                  <p className="text-info text-app-text-muted">This name is sent unencrypted in notifications. Leave blank for generic messages.</p>
                </div>
                <div className="space-y-1">
                  <Label>Description</Label>
                  <Field as={Textarea} name="description" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>Streak Factor</Label>
                    <Field as={Input} name="streak_factor" type="number" min={0} step={0.1} />
                  </div>
                  <div className="space-y-1">
                    <Label>Punishment Points</Label>
                    <Field as={Input} name="punishment_points" type="number" min={0} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Max Points (optional)</Label>
                  <Field as={Input} name="max_points" type="number" placeholder="No limit" />
                  <p className="text-info text-app-text-muted">Rule only enforced when partner's points are below this value. Leave empty for always active.</p>
                </div>
                <div className="space-y-1">
                  <Label>Auto-Expire Timer</Label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!values.expiry_days && !customExpiry ? "bg-app-primary text-white" : "bg-app-surface-raised text-app-text-muted hover:bg-app-surface-raised/80"}`}
                      onClick={() => { setFieldValue("expiry_days", ""); setCustomExpiry(false); }}
                    >
                      None
                    </button>
                    {EXPIRY_PRESETS.map((preset) => (
                      <button
                        key={preset.days}
                        type="button"
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!customExpiry && Number(values.expiry_days) === preset.days ? "bg-app-primary text-white" : "bg-app-surface-raised text-app-text-muted hover:bg-app-surface-raised/80"}`}
                        onClick={() => { setFieldValue("expiry_days", preset.days); setCustomExpiry(false); }}
                      >
                        {preset.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${customExpiry ? "bg-app-primary text-white" : "bg-app-surface-raised text-app-text-muted hover:bg-app-surface-raised/80"}`}
                      onClick={() => { setCustomExpiry(true); if (!values.expiry_days) setFieldValue("expiry_days", 2); }}
                    >
                      Custom
                    </button>
                  </div>
                  {customExpiry && (
                    <div className="mt-1.5">
                      <Field as={Input} name="expiry_days" type="number" min={1} placeholder="Days" />
                    </div>
                  )}
                  <p className="text-info text-app-text-muted">
                    Rule auto-deactivates after this duration. Saving restarts the timer.
                    {editingRule?.expires_at && new Date(editingRule.expires_at).getTime() > Date.now() && (
                      <span className="text-app-info font-medium ml-1">
                        ⏳ {formatTimeRemaining(editingRule.expires_at)}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2 justify-end flex-wrap">
                  {editingRule && editingRule.is_active && (
                    <Button type="button" variant="outline" className="border-app-danger text-app-danger hover:bg-app-danger/10 mr-auto" onClick={() => { setShowForm(false); setEditingRule(null); handleDelete(editingRule._key); }}>
                      Deactivate
                    </Button>
                  )}
                  {editingRule && !editingRule.is_active && (
                    <Button type="button" variant="outline" className="border-app-danger text-app-danger hover:bg-app-danger/10 mr-auto" onClick={() => { setShowForm(false); setEditingRule(null); handlePermanentDelete(editingRule._key); }}>
                      Delete Permanently
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingRule(null); setRestartTimerOnSave(false); setCustomExpiry(false); }}>Cancel</Button>
                  {editingRule && hasTimer ? (
                    <>
                      <Button type="button" variant="outline" className="border-app-primary text-app-primary hover:bg-app-primary/10" disabled={submitLoading}
                        onClick={() => { setRestartTimerOnSave(false); submitForm(); }}>
                        {submitLoading ? "Saving..." : "Save"}
                      </Button>
                      <Button type="button" className="bg-app-primary hover:bg-app-primary-hover" disabled={submitLoading}
                        onClick={() => { setRestartTimerOnSave(true); submitForm(); }}>
                        {submitLoading ? "Saving..." : "Save & Restart Timer"}
                      </Button>
                    </>
                  ) : (
                    <Button type="submit" className="bg-app-primary hover:bg-app-primary-hover" disabled={submitLoading}>
                      {submitLoading ? "Saving..." : editingRule ? "Update" : "Create"}
                    </Button>
                  )}
                </div>
              </Form>
              );
            }}
          </Formik>
        )}
      </BottomDrawer>

      {!rules || rules.length === 0 ? (
        <EmptyState icon="📏" title="No rules yet" description="Create your first rule!" />
      ) : (
        <div className="space-y-2">
          {[...rules]
            .sort((a, b) => {
              // Disabled at the very bottom
              if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
              // Among active: paused (due to filter) below active
              const aPaused = a.is_active && a.max_points != null && currentPoints >= a.max_points;
              const bPaused = b.is_active && b.max_points != null && currentPoints >= b.max_points;
              if (aPaused !== bPaused) return aPaused ? 1 : -1;
              // Paused: sort by max_points descending
              if (aPaused && bPaused) return (b.max_points ?? 0) - (a.max_points ?? 0);
              // Then by punishment_points descending
              return b.punishment_points - a.punishment_points;
            })
            .map((rule) => {
              const pointsPaused = rule.is_active && rule.max_points != null && currentPoints >= rule.max_points;
              const borderColor = !rule.is_active ? "border-l-app-text-muted" : pointsPaused ? "border-l-app-warning" : "border-l-app-info";
              const dimmed = !rule.is_active || pointsPaused;
              return (
            <Card key={rule._key} className={`border-l-4 ${borderColor} ${dimmed ? "opacity-60" : ""} hover:shadow-md hover:bg-app-surface-raised/50 transition-all duration-200 py-0 gap-0 cursor-pointer overflow-hidden`} onClick={() => { setEditingRule(rule); setShowForm(true); setCustomExpiry(rule.expiry_days != null && !EXPIRY_PRESETS.some((p) => p.days === rule.expiry_days)); }}>
              <CardContent className="p-2 md:p-2.5">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${!rule.is_active ? "bg-app-surface-raised" : pointsPaused ? "bg-app-warning/10" : "bg-app-info/10"} flex items-center justify-center text-lg shrink-0`}>{rule.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-body font-semibold">{rule.name}</h3>
                      {!rule.is_active ? (
                        <span className="text-xs bg-app-surface-raised text-app-text-muted px-2 py-0.5 rounded-full font-medium">Disabled</span>
                      ) : pointsPaused ? (
                        <span className="text-xs bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Paused above {rule.max_points} pts
                        </span>
                      ) : null}
                    </div>
                    {rule.description && (
                      <MarkdownText text={rule.description} className="text-info text-app-text-muted mt-0.5" />
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-xs bg-app-danger/10 text-app-danger px-2 py-0.5 rounded-full font-semibold">-{rule.punishment_points} per break</span>
                      <span className="text-xs bg-app-surface-raised px-2 py-0.5 rounded-full font-medium text-app-text-muted">×{rule.streak_factor} streak multiplier</span>
                      {rule.max_points != null && !pointsPaused && <span className="text-xs bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-medium">⚡ &lt;{rule.max_points} pts</span>}
                      {rule.is_active && rule.expires_at && new Date(rule.expires_at).getTime() > Date.now() && (
                        <span className="text-xs bg-app-info/10 text-app-info px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                          <Timer className="w-3 h-3" /> {formatTimeRemaining(rule.expires_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  {rule.is_active && !pointsPaused ? (
                  <Button variant="ghost" size="icon" className="shrink-0" onClick={(e) => { e.stopPropagation(); handleDelete(rule._key); }}>
                    <X className="w-4 h-4 text-app-danger" />
                  </Button>
                  ) : pointsPaused ? (
                  <div className="shrink-0 rounded-lg w-8 h-8 bg-app-warning/10 flex items-center justify-center" title={`Paused — points at or above ${rule.max_points}`}>
                    <Zap className="w-4 h-4 text-app-warning" />
                  </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
              );
            })}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
